import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PinInput from "./PinInput";

const API_BASE = import.meta.env.VITE_API_URL || "";
const authGet  = (path) => axios.get(`${API_BASE}/api${path}`, {
  headers: { Authorization: `Bearer ${localStorage.getItem("user_token")}` },
});

export default function PaymentConfirmSheet({
  open,
  onClose,
  onConfirm,
  loading = false,
  pinError = "",
  amount        = 0,
  netAmount,
  couponSaving  = 0,
  cashbackUsed  = 0,
  rows          = [],
  confirmLabel  = "Confirm Payment",
}) {
  const navigate  = useNavigate();
  const charged   = netAmount ?? amount;
  const saved     = couponSaving + cashbackUsed;

  const [step,   setStep]   = useState(1);
  const [pin,    setPin]    = useState("");
  const [hasPin, setHasPin] = useState(null);

  useEffect(() => {
    if (pinError) setPin("");
  }, [pinError]);

  useEffect(() => {
    if (!open) return;
    setStep(1); setPin("");
    authGet("/mtrade/user/transaction-pin/status")
      .then(r => setHasPin(r.data.hasPin))
      .catch(() => setHasPin(false));
  }, [open]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const handleBackdrop = (e) => {
    if (!loading && e.target === e.currentTarget) onClose();
  };

  const handlePinChange = (val) => {
    setPin(val);
    if (val.length === 4) handleSubmit(val);
  };

  const handleSubmit = (pinVal) => {
    const p = pinVal || pin;
    if (p.length !== 4) return;
    onConfirm(p);
  };

  const handleReviewConfirm = () => {
    if (hasPin === false) {
      onClose();
      navigate("/create-pin", { state: { redirectTo: window.location.pathname } });
      return;
    }
    setStep(2);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: "var(--color-overlay)" }}
      onClick={handleBackdrop}
    >
      <div
        className="w-full max-w-md bg-surface rounded-t-3xl overflow-hidden"
        style={{ animation: "slideUp 280ms cubic-bezier(0.32,0.72,0,1)" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Handle + header */}
        <div className="flex flex-col items-center pt-3 pb-4 px-5 border-b border-border">
          <div className="w-10 h-1 rounded-full bg-border-strong mb-4" />
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              {step === 2 && (
                <button
                  onClick={() => { setStep(1); setPin(""); }}
                  className="w-8 h-8 rounded-xl bg-surface-hover flex items-center justify-center"
                >
                  <i className="ri-arrow-left-line text-secondary text-base" />
                </button>
              )}
              <p className="text-primary font-bold text-base font-display">
                {step === 1 ? "Confirm Payment" : "Enter PIN"}
              </p>
            </div>
            {!loading && (
              <button onClick={onClose} className="w-8 h-8 rounded-xl bg-surface-hover flex items-center justify-center">
                <i className="ri-close-line text-secondary text-lg" />
              </button>
            )}
          </div>
        </div>

        {/* ── STEP 1: Review ─────────────────────────────────── */}
        {step === 1 && (
          <div className="px-5 pt-5 pb-6 space-y-5">
            <div className="text-center">
              <p className="text-muted text-xs font-medium mb-1">You pay</p>
              <p className="text-primary font-bold font-display" style={{ fontSize: "2.25rem" }}>
                ₦{charged.toLocaleString()}
              </p>
              {saved > 0 && (
                <div className="flex items-center justify-center gap-1.5 mt-1">
                  <span className="text-muted text-xs line-through">₦{amount.toLocaleString()}</span>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-success-light" style={{ color: "var(--color-success-text)" }}>
                    Save ₦{saved.toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            <div className="bg-surface-muted rounded-2xl overflow-hidden divide-y divide-border">
              
              {rows.some(r => r.critical) && (
                <div className="flex items-start gap-2 px-4 py-3 bg-warning-light border border-warning-border rounded-2xl">
                  <i className="ri-error-warning-line text-base flex-shrink-0 mt-0.5" style={{ color: "var(--color-warning-text)" }} />
                  <p className="text-xs font-medium" style={{ color: "var(--color-warning-text)" }}>
                    Ensure the phone number and network is correct before you continue.
                  </p>
                </div>
              )}
              <div className="bg-surface-muted rounded-2xl overflow-hidden divide-y divide-border">
                {rows.map((row, i) => (
                  <div
                  key={i}
                  className={`flex items-start justify-between px-4 py-3 gap-3 ${row.critical ? "bg-warning-light" : ""}`}
                  >
                    <span className="text-muted text-sm flex-shrink-0">{row.label}</span>
                    <span className={`text-primary text-sm font-semibold text-right break-all ${row.mono ? "font-mono" : ""}`}>
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {(couponSaving > 0 || cashbackUsed > 0) && (
              <div className="bg-success-light border border-success-border rounded-2xl overflow-hidden divide-y" style={{ borderColor: "var(--color-success-border)" }}>
                {cashbackUsed > 0 && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <i className="ri-hand-coin-line text-sm" style={{ color: "var(--color-success-text)" }} />
                      <span className="text-sm font-medium" style={{ color: "var(--color-success-text)" }}>Cashback</span>
                    </div>
                    <span className="text-sm font-bold" style={{ color: "var(--color-success)" }}>−₦{cashbackUsed.toLocaleString()}</span>
                  </div>
                )}
                {couponSaving > 0 && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <i className="ri-coupon-3-line text-sm" style={{ color: "var(--color-success-text)" }} />
                      <span className="text-sm font-medium" style={{ color: "var(--color-success-text)" }}>Coupon</span>
                    </div>
                    <span className="text-sm font-bold" style={{ color: "var(--color-success)" }}>−₦{couponSaving.toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}

            {/* Payment method — mirrors PalmPay's "wallet name + balance + check" row */}
            <div className="flex items-center justify-between px-4 py-3 bg-surface-muted rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "var(--color-brand-light)" }}>
                  <i className="ri-wallet-3-line text-base" style={{ color: "var(--color-brand)" }} />
                </div>
                <div>
                  <p className="text-secondary text-sm font-semibold">Easwallet Balance</p>
                  <p className="text-muted text-xs">Main wallet</p>
                </div>
              </div>
              <i className="ri-check-line text-xl" style={{ color: "var(--color-brand)" }} />
            </div>

            <button
              onClick={handleReviewConfirm}
              disabled={hasPin === null}
              className="w-full bg-brand hover:bg-brand-hover disabled:opacity-50 text-on-brand py-4 rounded-2xl font-bold text-sm transition-all active:scale-[0.98] flex items-center justify-center gap-2"
            >
              {hasPin === null ? (
                <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : hasPin ? (
                <><i className="ri-shield-keyhole-line text-base" />Continue · Enter PIN</>
              ) : (
                <><i className="ri-add-circle-line text-base" />Create PIN to continue</>
              )}
            </button>
          </div>
        )}

        {/* ── STEP 2: PIN entry ──────────────────────────────── */}
        {step === 2 && (
          <div className="px-5 pt-6 pb-8 flex flex-col items-center">
            <div className="bg-surface-muted rounded-2xl px-6 py-3 text-center mb-8">
              <p className="text-muted text-xs mb-0.5">Paying</p>
              <p className="text-primary font-bold text-2xl font-display">
                ₦{charged.toLocaleString()}
              </p>
            </div>

            <PinInput
              value={pin}
              onChange={handlePinChange}
              label="Enter your transaction PIN"
              error={pinError || undefined}
              disabled={loading}
            />

            {loading && (
              <div className="mt-6 flex items-center gap-2" style={{ color: "var(--color-brand)" }}>
                <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                  style={{ borderColor: "var(--color-brand)", borderTopColor: "transparent" }} />
                <span className="text-sm font-medium">Processing payment…</span>
              </div>
            )}

            <button
              onClick={() => navigate("/change-pin")}
              className="mt-6 text-muted text-xs font-medium hover:opacity-80 transition-colors"
            >
              Forgot PIN? Change PIN
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);   opacity: 1; }
        }
      `}</style>
    </div>
  );
}